import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Validators } from '@angular/forms';

import { DialogService } from 'primeng/dynamicdialog';

import { filter, Subject, switchMap, takeUntil } from 'rxjs';

import { IPaperReferencesData } from 'editing-tool/src/app/shared/components/paper/steps/references/paper-references-data';
import { IReuploadReferencesDialogData } from 'editing-tool/src/app/shared/components/paper/steps/references/reupload-references-dialog/reupload-references-dialog-data';
import { ReuploadReferencesDialogComponent } from 'editing-tool/src/app/shared/components/paper/steps/references/reupload-references-dialog/reupload-references-dialog.component';
import { BackgroundProcessEnum, BackgroundProcessService, Renderers, TableSettings, ValidationConstants } from 'sc-common';
import { ApiEditingToolReferenceListItem } from 'sc-common/core/services/open-api/main-api-client';

@Component({
    selector: 'et-paper-references',
    templateUrl: './paper-references.component.html',
    standalone: false
})
export class PaperReferencesComponent implements OnInit, OnDestroy {

    @Input()
    public data: IPaperReferencesData;

    @Input()
    public isReadonly: boolean;

    @Output()
    public readonly referencesCountChanged = new EventEmitter();

    public tableSettings: TableSettings<ApiEditingToolReferenceListItem>;

    private readonly _destroy$ = new Subject<void>();

    private readonly _refreshTable$ = new Subject<void>();

    constructor(
        private readonly _dialogService: DialogService,
        bgProcessService: BackgroundProcessService) {

        bgProcessService.onComplete(BackgroundProcessEnum.LoadReferencesManually)
            .pipe(
                switchMap(message => this.data.filterBackgroundProcesses(message.jobData)),
                filter(x => !!x),
                takeUntil(this._destroy$))
            .subscribe(() => this._refreshTable$.next());
    }

    public ngOnInit(): void {
        this._initTable();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public reupload(): void {

        const dialogData: IReuploadReferencesDialogData = {
            validationRules$: this.data.validationRules$,
            reUploadUrl$: this.data.reUploadUrl$
        };

        this._dialogService.open(ReuploadReferencesDialogComponent, {
            header: $localize`Reupload references`,
            width: '30vw',
            closable: true,
            data: dialogData
        });
    }

    private _initTable(): void {

        this.tableSettings = TableSettings.create(ApiEditingToolReferenceListItem, {
            dataSource: oDataQuery => this.data.getList(oDataQuery),
            defaultSort: {
                field: m => m.displayOrder,
                order: 'asc'
            },
            columnResizeMode: 'fit',
            paginator: false,
            rowHover: false,
            hasFilters: false,
            columns: {
                displayOrder: {
                    header: $localize`#`,
                    width: '50px',
                    centered: true,
                    sortable: false,
                    filterable: false
                },
                reference: {
                    editable: this.isReadonly ?
                        null :
                        {
                            callback: (_, item) => this.data.update(item),
                            validators: [Validators.required, Validators.maxLength(ValidationConstants.Max1024Length)],
                            showIcon: true,
                            showValidationTooltip: true
                        },
                    renderer: Renderers.text(null, null, true),
                    sortable: false,
                    filterable: false,
                    wrap: true
                },
                doi: {
                    renderer: Renderers.externalLink((_, doi) => `https://www.doi.org/${ doi }`, true),
                    width: '350px',
                    sortable: false,
                    filterable: false
                }
            },
            reload$: this._refreshTable$
        });
    }
}
